import { useState } from 'react';

import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import { useDeleteChronologyFactWithSync } from '../../../../../../../api/mutations/useDeleteChronologyFactWithSync';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateChronologyFactWithSync from '@/api/mutations/useUpdateChronologyFactWithSync';
import useVerifyChronologyEvent from '@/api/mutations/useVerifyChronologyFact';
import { QueryEntity } from '@/api/types';
import Accordion from '@/components/molecules/Accordion';
import { ConfirmationModal } from '@/components/molecules/Modals/Confirmation';
import FactDetail from '@/components/organisms/FactDropdown/FactDetail';
import FactTitle from '@/components/organisms/FactDropdown/FactTitle';
import { ChronologyFact } from '@/types';

const ChronologyDropdown = ({ fact, caseId }: { fact: ChronologyFact; caseId: string }) => {
  const [searchParams] = useSearchParams();
  const chronologyId = searchParams.get('chronologyId') || '';

  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [verified, setVerified] = useState(fact.verifiedById !== null);
  const [verifiedDate, setVerifiedDate] = useState(fact.verifiedDate ?? undefined);
  const [verifiedBy, setVerifiedBy] = useState(fact.verifiedBy?.email);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { mutateAsync: updateChronologyFact, isLoading: isUpdatingChronologyFact } = useUpdateChronologyFactWithSync({
    caseId: caseId,
  });
  const { mutateAsync: updateChronologySignificance, isLoading: isUpdatingChronologySignificance } =
    useUpdateChronologyFactWithSync({
      caseId: caseId,
    });
  const { mutateAsync: deleteChronologyFact, isLoading: isRemovingFact } = useDeleteChronologyFactWithSync(
    caseId ?? '',
    chronologyId,
  );
  const { mutateAsync: updateVerifiedMutation } = useVerifyChronologyEvent(caseId);

  const handleSuccess = () => {
    if (chronologyId) {
      queryClient.invalidateQueries([QueryEntity.ChronologyFacts, { chronologyId }]);
    }
  };

  const handleToggleVerify = async (verify: boolean) => {
    setVerified(verify);
    setVerifiedDate(new Date());
    setVerifiedBy(fact.verifiedBy?.email || 'you');
    await updateVerifiedMutation({ factId: fact.id, verified: verify });
    handleSuccess();
  };

  const handleUpdateDate = async (value: string) => {
    await updateChronologyFact({
      field: 'factDateText',
      value,
      factId: fact.id,
      chronologyId: fact.chronologyId,
    });
    handleSuccess();
  };

  const handleUpdateFactText = async (value: string) => {
    await updateChronologyFact({
      field: 'description',
      value,
      factId: fact.id,
      chronologyId: fact.chronologyId,
    });
    handleSuccess();
  };

  const handleRemoveRow = async () => {
    trackCustomEvent(TrackingEvents.RemoveFactFromChronology, { chronologyId, caseId });

    try {
      await deleteChronologyFact(fact.id);
    } catch (err) {
      console.error('Fetch Error: ', err);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const handleUpdateSignificanceText = async (value: string) => {
    await updateChronologySignificance({
      field: 'significance',
      value,
      factId: fact.id,
      chronologyId: fact.chronologyId,
    });
    handleSuccess();
  };

  const factDeleteDescription = (
    <div>
      <p className="text-sm">Are you sure you want to remove this fact from the chronology?</p>

      <p className="pt-3 text-sm text-gray-500">The underlying fact will stil exist.</p>
    </div>
  );

  return (
    <>
      <Accordion
        title={
          <FactTitle
            fact={fact}
            factText={fact.description}
            dateText={fact.factDateText}
            relevance={fact.relevance}
            open={open}
            compressedVersion={true}
            updateFactDateText={handleUpdateDate}
            updateFactDescription={handleUpdateFactText}
            isUpdatingDateText={isUpdatingChronologyFact}
            isUpdatingDescription={isUpdatingChronologyFact}
            verification={{
              verified,
              handleToggleVerify,
              email: verifiedBy,
              date: verifiedDate,
            }}
          />
        }
        content={
          <FactDetail
            fact={fact.fact}
            significanceText={fact.significance ?? ''}
            relevanceText={fact.relevance}
            updateSignificance={handleUpdateSignificanceText}
            isUpdatingSignificance={isUpdatingChronologySignificance}
            compressedVersion={true}
            handleOpenDeleteModal={() => setIsDeleteModalOpen(true)}
          />
        }
        className="relative p-0"
        outerOpen={setOpen}
      />

      <ConfirmationModal
        title={'Remove Fact'}
        isOpen={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        description={factDeleteDescription}
        onConfirm={handleRemoveRow}
        isLoading={isRemovingFact}
        confirmButtonText="Remove Fact"
        confirmButtonVariant="destructive"
      />
    </>
  );
};

export default ChronologyDropdown;
